import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const normalizeCatalogue = (catalogue, index) => {
  const normalized = {
    id: catalogue.id || catalogue._id || `catalogue-${index + 1}`,
    title: catalogue.title || catalogue.name || 'Catalogue',
    subtitle: catalogue.subtitle || catalogue.fileName || 'Product catalogue',
    pages: typeof catalogue.pages === 'number' ? catalogue.pages : 0,
    size: catalogue.size || catalogue.fileSize || '',
    icon: catalogue.icon || 'fa-book-open',
    desc: catalogue.desc || catalogue.description || '',
    img: catalogue.img
      ? catalogue.img.startsWith('http')
        ? catalogue.img
        : `${process.env.PUBLIC_URL}${catalogue.img}`
      : catalogue.coverImage
        ? catalogue.coverImage.startsWith('http')
          ? catalogue.coverImage
          : `${process.env.PUBLIC_URL}${catalogue.coverImage}`
        : `${process.env.PUBLIC_URL}/images/catalogues-img1.jpg`,
    category: catalogue.category || catalogue.shopCategorySlug || 'all',
    editionLabel: catalogue.editionLabel || '2025 Edition',
    downloadUrl: catalogue.downloadUrl || catalogue.pdfUrl || catalogue.pdf || '',
    fileName: catalogue.fileName || '',
  };
  
  console.log(`Normalized catalogue ${index}:`, normalized);
  return normalized;
};

export default function CataloguePage() {
  const [hoveredId, setHoveredId] = useState(null);
  const [catalogues, setCatalogues] = useState([]);
  const [bundle, setBundle] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCatalogues = async () => {
      try {
        const response = await api.get('/catalogues');
        const cataloguesData = response.data.catalogues || response.data || [];        

        if (Array.isArray(cataloguesData) && cataloguesData.length > 0) {
          const loadedCatalogues = cataloguesData.map(normalizeCatalogue);
          setCatalogues(loadedCatalogues);
        } else {
          console.warn('No catalogues found in response');
          setCatalogues([]);
        }

        if (response.data.bundle) {
          setBundle(response.data.bundle);
        }
      } catch (error) {
        console.error('Error loading catalogues:', error);
        setCatalogues([]);
      } finally {
        setLoading(false);
      }
    };

    loadCatalogues();
  }, []);

  const derivedBundleDescription = useMemo(() => {
    if (bundle.description) return bundle.description;
    const totalPages = catalogues.reduce((sum, item) => sum + item.pages, 0);
    return `Get all ${catalogues.length} catalogues in one collection — ${totalPages} pages ready to download.`;
  }, [bundle.description, catalogues]);

  const handleDownload = (catalogue) => {    
    if (!catalogue.downloadUrl || !catalogue.downloadUrl.trim()) {
      console.warn('No download URL available for:', catalogue.title);
      alert(`Download link is not configured for "${catalogue.title}".`);
      return;
    }

    // Construct full URL by removing /api from REACT_APP_API_URL
    const baseUrl = process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
    const url = baseUrl + catalogue.downloadUrl;
    // Try to download the file
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = catalogue.fileName || `${catalogue.title}.pdf`;
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      console.log('Fallback: opening in new window');
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleBundleDownload = () => {
    if (bundle.downloadUrl) {
      window.open(bundle.downloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    alert('Bundle download is not configured.');
  };

  return (
    <div className="catalogue-page-container">
      {/* Header */}
      <div className="catalogue-header">
        <div className="catalogue-header-overlay" />
        <div className="container text-center catalogue-header-content">
          <p>Pandian Store</p>
          <h1>Our <em>Digital Catalogues</em></h1>
          <p className="catalogue-header-desc">
            Browse our complete product range in beautifully designed digital catalogues — available to download anytime
          </p>
        </div>
      </div>
      
      {/* Catalogue grid */}
      <section className="section-pad catalogue-grid-section">
        <div className="container">
          {loading ? (
            <p className="text-center">Loading catalogues...</p>
          ) : catalogues.length === 0 ? (
            <p className="text-center">No catalogues available right now.</p>
          ) : (
          <div className="row g-4">

            {catalogues.map((cat) => (
              <div key={cat.id} className={`col-md-6 col-lg-3 catalogue-card-col ${cat.category}`}>
                <div
                  className="catalogue-card"
                  onMouseEnter={() => setHoveredId(cat.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Cover */}
                  <div className="catalogue-cover">
                    <div className="catalogue-cover-lines" />
                    <img
                      src={cat.img} 
                      alt={cat.title}
                      className={`catalogue-cover-img ${hoveredId === cat.id ? 'is-hovered' : ''}`}
                    />
                    <div className="catalogue-cover-content">
                      <div className="catalogue-icon-circle">
                        <i className={`fas ${cat.icon}`} />
                      </div>
                      <p className="catalogue-edition-text">{cat.editionLabel}</p>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="catalogue-card-body">
                    <h3>{cat.title}</h3>
                    <p className="subtitle">{cat.subtitle}</p>
                    <p className="desc">{cat.desc}</p>
                    
                    <div className="catalogue-meta-group">
                      <span className="catalogue-meta-item">
                        <i className="fas fa-file-lines" />{cat.pages} pages
                      </span>
                      <span className="catalogue-meta-item">
                        <i className="fas fa-download" />{cat.size}
                      </span>
                    </div>

                    <div className="catalogue-action-group">
                      <button
                        className="btn-gold catalogue-download-btn"
                        onClick={() => handleDownload(cat)}
                        type="button"
                        style={{
                          padding: '10px 20px',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: '#FFD700',
                        }}
                      >
                        <i className="fas fa-download" /> Download
                      </button>
                      <Link 
                        to={`/shop?category=${cat.category}`}
                        className="catalogue-shop-link"
                        title="Browse products"
                      >
                        <i className="fas fa-arrow-right" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}

          {/* All-in-one CTA — only shown when bundle data is available
          {catalogues.length > 0 && bundle.title && (
          <div className="catalogue-bundle-cta">
            <div>
              <p className="bundle-sub">Complete Collection</p>
              <h2>{bundle.title}</h2>
              <p className="bundle-desc">
                {derivedBundleDescription}
              </p>
            </div>
            <button
              className="btn-gold catalogue-bundle-btn"
              onClick={handleBundleDownload}
            >
              <i className="fas fa-file-zipper catalogue-bundle-cta-mr-8" /> Download Bundle
            </button>
          </div>
          )} */}
        </div>
      </section>
    </div>
  );
}